import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface LensBlurParams {
  radius: number;
  bladeCurvature: number;
  rotation: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const LensBlur: Filter<LensBlurParams> = {
  id: "lens-blur",
  displayName: "Lens Blur",
  defaultParams: { radius: 10, bladeCurvature: 50, rotation: 0 },

  apply(input: PixelBuffer, params: LensBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0 || params.radius < 1) { dst.set(src); return out; }

    const r = Math.round(params.radius);
    const blades = 6;
    const curvature = params.bladeCurvature / 100;
    const rot = params.rotation * Math.PI / 180;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > r) continue;

            const angle = Math.atan2(dy, dx) + rot;
            const bladeAngle = ((angle % (Math.PI * 2 / blades)) + Math.PI * 2 / blades) % (Math.PI * 2 / blades);
            const bladeDist = r * (1 - curvature * Math.abs(bladeAngle - Math.PI / blades) / (Math.PI / blades));

            if (dist <= bladeDist) {
              const sx = Math.max(0, Math.min(w - 1, x + dx));
              const sy = Math.max(0, Math.min(h - 1, y + dy));
              const si = (sy * w + sx) * 4;
              sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
              count++;
            }
          }
        }

        const o = (y * w + x) * 4;
        if (count > 0) {
          dst[o] = clamp8(Math.round(sumR / count));
          dst[o + 1] = clamp8(Math.round(sumG / count));
          dst[o + 2] = clamp8(Math.round(sumB / count));
          dst[o + 3] = clamp8(Math.round(sumA / count));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
